import { useFormik } from 'formik'
import Page from '../layout/Page/Page'
import PageWrapper from '../layout/PageWrapper/PageWrapper'
import { getCurrentDateAndTime, onlyAllowNumber } from '../services/common.service'
import Card, { CardHeader, CardLabel, CardTitle, CardBody, CardActions, CardFooter, CardFooterRight } from './bootstrap/Card'
import { useContext, useEffect, useState } from 'react'
import { addInvoice, getDeliveryChallanList } from '../services/productService'
import { toasts } from '../services/toaste.service'
import AuthContext from '../contexts/authContext'
import FormGroup from './bootstrap/forms/FormGroup'
import Input from './bootstrap/forms/Input'
import Textarea from './bootstrap/forms/Textarea'
import { useParams } from 'react-router-dom'
import InputGroup, { InputGroupText } from './bootstrap/forms/InputGroup'
import SearchableSelect from '../common/components/SearchableSelect'
import NoDataMsg from '../common/components/NoDataMsg'
import { getLicenseKey } from '../services/application.settings'
import { showLoader } from '../services/loader.services'
import AlertService from '../services/alert.service'
import Button from './bootstrap/Button'

const AddInvoiceDetails = () => {

  const { deliveryChallanId } = useParams();

  useEffect(() => {
    getDeliveryChallan()
    getCompanyList()
  }, [])

  const challanDetailsForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      invoiceDate: getCurrentDateAndTime('date'),
      invoiceFrom: '',
      poNumber: '',
      transportMode: ''
    },
    validate: (values: any) => {
      const errors: {
        invoiceDate?: string;
        poNumber?: string;
        transportMode?: string;
        invoiceFrom?: string;
      } = {};
      if (!values.invoiceDate) {
        errors.invoiceDate = 'Required';
      }
      if (!values.poNumber) {
        errors.poNumber = 'Required';
      }
      if (!values.transportMode) {
        errors.transportMode = 'Required';
      }
      if (!companyDetailsId) {
        errors.invoiceFrom = 'Required';
      }
      return errors;
    },
    //validateOnChange: false,
    onSubmit: () => { addInvoiceDetails() },
  });

  const { userAccountId, userTypeId } = useContext(AuthContext);
  const [deliveryChallanDetailsData, setDeliveryChallanDetailsData] = useState<any>([])
  const [noDataMsg, setNoDataMsg] = useState('')
  const [inputList, setInputList] = useState<any>([])
  const [navigate, setNavigate] = useState<any>(null)

  const [isOpen, setIsOpen] = useState(false);
  const [alertStatus, setAlertStatus] = useState<any>({ message: "", type: "" });

  function getDeliveryChallan() {
    getDeliveryChallanList(deliveryChallanId,
      (response) => {
        if (response.data.success) {
          let data = response.data.data.deliveryChallanList ? response.data.data.deliveryChallanList[0] : response.data.data.invoiceDetails ? response.data.data.invoiceDetails[0] : undefined;
          if (data != undefined) {
            // Pre-fill form fields if it's an existing invoice
            if (data.invoiceDate) challanDetailsForm.setFieldValue('invoiceDate', data.invoiceDate);
            if (data.poNumber) challanDetailsForm.setFieldValue('poNumber', data.poNumber);
            if (data.transportMode) challanDetailsForm.setFieldValue('transportMode', data.transportMode);
            if (data.companyDetailsId) setCompanyDetailsId({ value: data.companyDetailsId, label: data.companyName });

            let items = data.productDetails || data.invoiceItems || [];
            let normalizedItems = items.map((item: any) => {
              const finalAmount = Number(item.finalAmount ?? item.totalAmount ?? 0);
              const discPerc = Number(item.discountPercentage ?? 0);
              const taxRate = Number(item.igstRate ?? 0);

              const taxableValue = finalAmount - (finalAmount * discPerc / 100);
              const taxAmount = (taxableValue * taxRate) / 100;
              const finalValue = taxableValue + taxAmount;

              return {
                ...item,
                finalAmount: finalAmount.toFixed(2),
                discountPercentage: discPerc,
                discountAmount: taxableValue.toFixed(2),
                igstRate: taxRate,
                igstAmount: taxAmount > 0 ? taxAmount.toFixed(2) : '',
                finalValue: finalValue.toFixed(2)
              };
            });

            setDeliveryChallanDetailsData(data)
            setInputList(normalizedItems)

          } else {
            toasts("Undefined Data", "Error")
          }
        }
        else if (response.data.success === false) {
          setNoDataMsg(response.data.message)
        } else {
          let errorDescription = response.data.error[0].error_description;
          toasts(errorDescription, "Error")
        }
      }, error => {
        toasts(error, "Error")
      }
    )
  }

  function setInvoiceDetails() {
    return ({
      licenseKey: getLicenseKey,
      invoiceFrom: companyDetailsId?.value,
      deliveryChallanId: deliveryChallanId,
      invoiceId: deliveryChallanDetailsData?.invoiceId || 0,
      invoiceUniqueNo: deliveryChallanDetailsData?.invoiceUniqueNo || '',
      invoiceDate: challanDetailsForm.values.invoiceDate,
      poNumber: challanDetailsForm.values.poNumber,
      transportMode: challanDetailsForm.values.transportMode,
      userAccountId: userAccountId,
      invoiceDetails: inputList
    })
  }

  function addInvoiceDetails() {
    if (inputList.length > 0) {
      showLoader(true)
      let invoicePostData = setInvoiceDetails()
      if (challanDetailsForm.isValid) {
        addInvoice(invoicePostData,
          (response) => {
            const data = response.data;
            if (data.success == true) {
              showLoader(false)
              setAlertStatus({ message: data.message, type: "success" });
              setIsOpen(true);
              setNavigate('../invoiceList')
              closeAndReset()
            }
            else if (data.success == false) {
              showLoader(false)
              setAlertStatus({ message: data.message, type: "error" });
              setIsOpen(true);
            }
            else {
              let errorCode = response.data.error[0].error_code;
              let errorDescription = response.data.error[0].error_description;
              showLoader(false)
              setAlertStatus({ message: errorDescription, type: "error" });
              setIsOpen(true);
            }
          }
          , (error) => {
            showLoader(false)
            setAlertStatus({ message: error, type: "error" });
            setIsOpen(true);
          }
        )
      } else if (challanDetailsForm.isValid == false) {
        toasts("Please fill all details", "Error");
      }
    }
    else {
      toasts("Please Enter Dispatch Details", "Error");
    }
  }

  function handleInputChange(data: any, discountPercentage: any, igstRate: any, index: any) {
    const list = [...inputList];
    const grossAmount = Number(data?.finalAmount || 0);
    const discPerc = Number(discountPercentage || 0);
    const taxRate = Number(igstRate || 0);

    const discountReduction = (grossAmount * discPerc) / 100;
    const taxableValue = grossAmount - discountReduction;
    const taxAmount = (taxableValue * taxRate) / 100;
    const finalValue = taxableValue + taxAmount;

    list[index]["discountPercentage"] = discountPercentage;
    list[index]["discountAmount"] = taxableValue.toFixed(2);
    list[index]["igstRate"] = igstRate;
    list[index]["igstAmount"] = taxAmount > 0 ? taxAmount.toFixed(2) : '';
    list[index]["finalValue"] = finalValue.toFixed(2);
    setInputList(list);
  }

  function closeAndReset() {
    challanDetailsForm.resetForm()
    setInputList([])
  }

  const [companyDetailsData, setCompanyDetailsData] = useState<any>([]);
  const [companyDetailsId, setCompanyDetailsId] = useState<any>('');

  function getCompanyList() {
    setCompanyDetailsData([
      { companyDetailsId: 1, companyName: 'ACV PRODUCTS PRIVATE LIMITED' },
      { companyDetailsId: 2, companyName: 'AVR TERRY TEX' }
    ])
  }

  // console.log(inputList)

  return (
    <PageWrapper title={`Invoice`}>
      <Page>
        <Card stretch data-tour='list'>
          <CardHeader borderSize={1}>
            <CardLabel icon='List' iconColor='info' className="col-lg-7">
              <CardTitle tag='div' className='h5'>Invoice Creation</CardTitle>
            </CardLabel>
          </CardHeader>
          <CardBody>
            <div className='row g-4'>
              <div className="col-lg-6">
                <div className="row g-3">
                  <div className="col-lg-6">
                    <FormGroup id='invoiceFrom' label='Invoice From' isFloating>
                      <SearchableSelect
                        isFloating
                        ariaLabel='invoiceFrom'
                        onChange={(e: any) => setCompanyDetailsId(e)}
                        value={companyDetailsId}
                        onBlur={challanDetailsForm.handleBlur}
                        isValid={challanDetailsForm.isValid}
                        isTouched={challanDetailsForm.touched.invoiceFrom}
                        invalidFeedback={challanDetailsForm.errors.invoiceFrom}
                        list={companyDetailsData.map((data: any) => ({
                          value: data.companyDetailsId,
                          label: data.companyName,
                        }))}
                        required
                      />
                    </FormGroup>
                  </div>
                  <div className='col-lg-6'>
                    <FormGroup id='companyName' label='Company Name' isFloating>
                      <Input value={deliveryChallanDetailsData?.companyName || ''} disabled />
                    </FormGroup>
                  </div>
                  {/* <div className='col-lg-6'>
                    <FormGroup id='companyName' label='Place of Supply' isFloating>
                      <Input value={deliveryChallanDetailsData?.companyNameForPlaceOfSupply || ''} disabled />
                    </FormGroup>
                  </div> */}
                  <div className='col-lg-6'>
                    <FormGroup id='invoiceDate' label='Invoice Date' isFloating>
                      <Input value={challanDetailsForm.values.invoiceDate || ''} disabled />
                    </FormGroup>
                  </div>
                  <div className='col-lg-6'>
                    <FormGroup id='truckNo' label='Truck No' isFloating>
                      <Input value={deliveryChallanDetailsData?.truckNo || ''} disabled />
                    </FormGroup>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row g-3">
                  <div className='col-lg-12'>
                    <FormGroup id='weavingProcess' label='Weaving Process' isFloating>
                      <Input
                        value={deliveryChallanDetailsData?.weavingProcess || ''}
                        placeholder="Enter Weaving Process"
                        disabled />
                    </FormGroup>
                  </div>
                  <div className="col-lg-6">
                    <FormGroup id='poNumber' label='PO Number' isFloating>
                      <Input
                        value={challanDetailsForm.values.poNumber}
                        onChange={challanDetailsForm.handleChange}
                        onBlur={challanDetailsForm.handleBlur}
                        isValid={challanDetailsForm.isValid}
                        isTouched={challanDetailsForm.touched.poNumber}
                        invalidFeedback={challanDetailsForm.errors.poNumber}
                        placeholder="Enter PO Number" />
                    </FormGroup>
                  </div>
                  <div className="col-lg-6">
                    <FormGroup id='transportMode' label='Transport Mode' isFloating>
                      <Input
                        value={challanDetailsForm.values.transportMode}
                        onChange={challanDetailsForm.handleChange}
                        onBlur={challanDetailsForm.handleBlur}
                        isValid={challanDetailsForm.isValid}
                        isTouched={challanDetailsForm.touched.transportMode}
                        invalidFeedback={challanDetailsForm.errors.transportMode}
                        placeholder="Enter Transport Mode" />
                    </FormGroup>
                  </div>
                </div>
              </div>
              <div className="row g-2">
                {inputList?.length > 0 ?
                  <>
                    <div className="table-responsive">
                      <table className='table table-modern table-hover small'>
                        <thead>
                          <tr>
                            <th scope='col'>S.No</th>
                            <th scope='col'>Description of Goods</th>
                            <th scope='col'>HSN code</th>
                            <th scope='col'>No.of <br />Bags</th>
                            <th scope='col'>WT. / <br />Bag</th>
                            <th scope='col'>Qty / <br />Kgs</th>
                            <th scope='col'>Rate / <br />Kgs</th>
                            <th scope='col'>Amount</th>
                            <th scope='col'>Discount</th>
                            <th scope='col'>Taxable <br />Value</th>
                            <th scope='col' className='text-center'>IGST %</th>
                            <th scope='col' className='text-center'>IGST ₹</th>
                            <th scope='col'>Total Value</th>
                          </tr>
                        </thead>
                        {inputList.map((item: any, index: any) => (
                          <tbody key={index}>
                            <tr>
                              <td>{index + 1}</td>
                              <td>{item.countNo} <br /> Lot No : [{item.lotNo}]</td>
                              <td>{item.hsnCode}</td>
                              <td>{item.noOfBags}</td>
                              <td>{item.quantity}</td>
                              <td>{item.quantity}</td>
                              <td>{item.productRate}</td>
                              <td>{item.finalAmount}</td>
                              <td width={'8%'}>
                                <Input
                                  onChange={(e: any) => handleInputChange(item, e.target.value, item.igstRate, index)}
                                  value={item.discountPercentage || ''}
                                  placeholder="%"
                                  onKeyDown={(e: any) => onlyAllowNumber(e)} />
                              </td>
                              <td>{item.discountAmount > 0 ? item.discountAmount : item.finalAmount}</td>
                              <td width={'8%'}>
                                <Input
                                  onChange={(e: any) => handleInputChange(item, item.discountPercentage, e.target.value, index)}
                                  value={item.igstRate || ''}
                                  placeholder="%"
                                  onKeyDown={(e: any) => onlyAllowNumber(e)} />
                              </td>
                              <td width={'9%'}>
                                <Input
                                  onChange={(e: any) => handleInputChange(item, item.discountPercentage, item.igstRate, index)}
                                  value={item.igstAmount || ''}
                                  placeholder="₹"
                                  onKeyDown={(e: any) => onlyAllowNumber(e)} />
                              </td>
                              <td>{item.finalValue > 0 ? item.finalValue : item.finalAmount}</td>
                            </tr>
                          </tbody>
                        ))}
                      </table>
                    </div>
                  </> : <NoDataMsg columnsCount={10} msg={noDataMsg || 'No items found'} />
                }
              </div>
            </div>
          </CardBody>
          <CardFooter>
            <CardFooterRight>
              <Button
                color='info'
                type="submit"
                icon="Save"
                isDisable={!challanDetailsForm.isValid && !!challanDetailsForm.submitCount || inputList.length == 0}
                onClick={challanDetailsForm.handleSubmit}>
                Submit
              </Button>
            </CardFooterRight>
          </CardFooter>
        </Card>
      </Page>
      <AlertService setIsOpen={setIsOpen} isOpen={isOpen} alertStatus={alertStatus} isNavigate={navigate} />
    </PageWrapper>
  )
}

export default AddInvoiceDetails